import { useEffect, useRef } from "react";

/**
 * Canvas-based fireworks animation.
 * Pan-African palette: gold, red, green, cream + white sparks.
 *
 * Props:
 *   active     – boolean. When true, animation is running.
 *   duration   – ms. Auto-stops after this time (default 9000).
 *   intensity  – 'full' | 'calm' (default 'full'). 'calm' launches fewer, slower.
 */
export default function Fireworks({ active = true, duration = 9000, intensity = "full" }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const launchTimerRef = useRef(null);
  const stopTimerRef = useRef(null);

  // Pan-African celebration palette
  const COLORS = [
    "#D4AF37", // gold
    "#F7D878", // light gold
    "#E32636", // red
    "#FF6B6B", // soft red
    "#2EA44F", // green
    "#59D18E", // light green
    "#FDFBF7", // cream
    "#FFFFFF", // white sparkle
  ];

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const burst = (x, y, color, count) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.5 + Math.random() * 4.2;
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 70 + Math.random() * 40,
          age: 0,
          color,
          size: 1.5 + Math.random() * 1.8,
          gravity: 0.045,
        });
      }
    };

    const launch = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.offsetWidth;
      const h = canvasRef.current.offsetHeight;
      const x = w * (0.15 + Math.random() * 0.7);
      const y = h * (0.15 + Math.random() * 0.45);
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const count = intensity === "full" ? 60 + Math.floor(Math.random() * 40) : 32;
      burst(x, y, color, count);
      // occasional multi-color double-burst
      if (Math.random() < 0.35) {
        setTimeout(() => {
          const color2 = COLORS[Math.floor(Math.random() * COLORS.length)];
          burst(x + (Math.random() - 0.5) * 40, y + (Math.random() - 0.5) * 40, color2, Math.floor(count * 0.7));
        }, 180);
      }
    };

    const step = () => {
      if (!canvasRef.current) return;
      const w = canvasRef.current.offsetWidth;
      const h = canvasRef.current.offsetHeight;
      // trailing fade for comet tails
      ctx.fillStyle = "rgba(5, 5, 5, 0.22)";
      ctx.fillRect(0, 0, w, h);

      const alive = [];
      for (const p of particlesRef.current) {
        p.age += 1;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.vx *= 0.992;
        if (p.age >= p.life) continue;

        const t = 1 - p.age / p.life;
        ctx.globalAlpha = Math.max(0, t);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 12;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (0.5 + t * 0.8), 0, Math.PI * 2);
        ctx.fill();
        alive.push(p);
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      particlesRef.current = alive;
      rafRef.current = requestAnimationFrame(step);
    };

    // kick off: a quick salvo, then a steady cadence
    launch();
    setTimeout(launch, 300);
    setTimeout(launch, 650);
    const interval = intensity === "full" ? 520 : 950;
    launchTimerRef.current = setInterval(launch, interval);
    rafRef.current = requestAnimationFrame(step);

    stopTimerRef.current = setTimeout(() => {
      if (launchTimerRef.current) clearInterval(launchTimerRef.current);
    }, duration);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (launchTimerRef.current) clearInterval(launchTimerRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      particlesRef.current = [];
    };
  }, [active, duration, intensity]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="milestone-fireworks-canvas"
      aria-hidden="true"
    />
  );
}
